// MessagesMercureProvider.tsx
import React, { createContext, useContext, useMemo } from 'react'
import { useQueryClient, useInfiniteQuery } from '@tanstack/react-query'
import useMercureES from '@/hooks/useMercureES'
import useCurrentUser from '@/hooks/useCurrentUser'
import { usePetsitterUser } from '@/modules/PetsitterUser/hooks/usePetsitterUser'
import { Quote } from '@/data/PetSitter/Messages'
import {
  conversationsKey,
  updateConversationsListFromMercure,
  updateQuoteFromMercure,
} from '@/modules/PetSitter/messagesCache'
import { getConversationsAsync } from '@/modules/PetSitter/messagesServiceHelper'

type MessagesMercureContextValue = {}

const MessagesMercureContext = createContext<MessagesMercureContextValue | undefined>(undefined)

export const useMessagesMercure = () => {
  const ctx = useContext(MessagesMercureContext)
  if (!ctx) throw new Error('useMessagesMercure must be used within MessagesMercureProvider')
  return ctx
}

const extractPageNumber = (url?: string | null) => {
  if (!url) return null
  const m = url.match(/page=(\d+)(?!.*page=)/)
  return m ? parseInt(m[1], 10) : null
}

export const MessagesMercureProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { currentUser } = useCurrentUser()
  const { id: petSitterId } = usePetsitterUser()
  const queryClient = useQueryClient()

  /** 1) Global conversations list query (first page is enough for @topic) */
  const { data: convInfinite } = useInfiniteQuery(
    conversationsKey,
    ({ pageParam = 1 }) => getConversationsAsync(pageParam),
    {
      getNextPageParam: (lastPage) => {
        const next = extractPageNumber(lastPage?.['hydra:view']?.['hydra:next'])
        const last = extractPageNumber(lastPage?.['hydra:view']?.['hydra:last'])
        return next && last && next <= last ? next : undefined
      },
      staleTime: 60 * 1000,
      cacheTime: 30 * 60 * 1000, // (v5: gcTime)
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      enabled: !!(currentUser?.id || petSitterId),
    },
  )

  const listTopic = convInfinite?.pages?.[0]?.['@topic'] ?? null

  /** 2) SSE conversations (global) */
  useMercureES(
    listTopic,
    (data: any) => {
      updateConversationsListFromMercure(queryClient, data)
    },
    (err) => console.warn('[Mercure] conversations error:', err?.message),
    () => console.debug('[Mercure] conversations open'),
    { completeTopicPath: true },
  )

  /** 3) SSE quotes (global) */
  useMercureES(
    currentUser?.id || petSitterId ? `u/${currentUser?.id || petSitterId}/quotes` : null,
    (data: Quote) => {
      updateQuoteFromMercure(queryClient, data)
    },
    (err) => console.warn('[Mercure] quotes error:', err?.message),
    () => console.debug('[Mercure] quotes open'),
    { completeTopicPath: true },
  )

  const value = useMemo(() => ({}), [])

  return <MessagesMercureContext.Provider value={value}>{children}</MessagesMercureContext.Provider>
}
